import * as React from "react";
import { useMe, useUpdateMyProfile } from "@/api/me";
import { useAuthStore } from "@/store/authStore";

/**
 * MV2-368: silent browser-timezone capture.
 *
 * On first /me load per authenticated session, compares the user's
 * stored ``default_timezone`` against ``Intl.DateTimeFormat().
 * resolvedOptions().timeZone`` and fires a fire-and-forget
 * ``PATCH /me/profile`` if they differ. This populates a sensible
 * value for any user who hasn't manually set one, so the
 * account-event emails (password change, MFA enrolled / disabled)
 * render in the user's local tz instead of falling back to UTC.
 *
 * No UI surface — pure side-effect. Per-session ref guard prevents
 * re-firing on stale-while-revalidate refetches; the next session
 * (or a tz change) will re-evaluate.
 */
export function useTimezoneCapture(): void {
  const status = useAuthStore((s) => s.status);
  // MV2-1254 — while viewing as someone, /me is theirs: never write their
  // timezone (the read-only view would refuse it anyway). Runs for you after.
  const viewingAs = useAuthStore((s) => s.viewingAs != null);
  const meQ = useMe({ enabled: status === "authenticated" });
  const updateProfile = useUpdateMyProfile();
  const firedRef = React.useRef(false);

  React.useEffect(() => {
    if (status !== "authenticated") return;
    if (viewingAs) return;
    if (!meQ.data) return;
    if (firedRef.current) return;

    let browserTz: string | null = null;
    try {
      browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
      // Very old browsers may throw — silent skip, never block the UI.
      firedRef.current = true;
      return;
    }
    if (!browserTz) {
      firedRef.current = true;
      return;
    }

    if (meQ.data.default_timezone === browserTz) {
      firedRef.current = true;
      return;
    }

    firedRef.current = true;
    updateProfile.mutate(
      { default_timezone: browserTz },
      {
        onError: (err) => {
          // Server validates IANA strings; Intl always returns one,
          // so a 400 here is genuinely unexpected. Log but don't toast —
          // the worst case is account emails fall back to UTC, not
          // worth interrupting the session over.
          console.warn("[tz-capture] PATCH /me/profile failed:", err);
        },
      },
    );
  }, [status, viewingAs, meQ.data, updateProfile]);
}
